import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";
import { Eye, EyeOff, Phone, Mail, AlertCircle, ArrowLeft } from "lucide-react";
import { GooglePlacesAddress } from "./GooglePlacesAddress";
import { ModernLogo } from "./ModernLogo";

interface AuthFormProps {
  onSuccess: (userType: string) => void;
  defaultUserType?: "customer" | "tradesman";
}

export const AuthForm = ({ onSuccess, defaultUserType = "customer" }: AuthFormProps) => {
  const [userType, setUserType] = useState<"customer" | "tradesman">(defaultUserType);
  const [address, setAddress] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showSignInPassword, setShowSignInPassword] = useState(false);
  const [showSignUpPassword, setShowSignUpPassword] = useState(false);
  const [resendEmail, setResendEmail] = useState("");
  const [isResending, setIsResending] = useState(false);
  const [magicEmail, setMagicEmail] = useState("");
  const [isSendingMagic, setIsSendingMagic] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        if (error.message === "Email not confirmed") {
          toast({
            title: "Email not confirmed",
            description: "Please check your email and click the confirmation link before signing in. Check your spam folder too.",
            variant: "destructive",
          });
        } else if (error.message === "Invalid login credentials") {
          toast({
            title: "Invalid credentials",
            description: "The email or password you entered is incorrect. Please try again.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Sign in failed",
            description: error.message,
            variant: "destructive",
          });
        }
        return;
      }

      // Get user type from profile to determine redirect
      const { data: profile } = await supabase
        .from("profiles")
        .select("user_type")
        .eq("user_id", (await supabase.auth.getUser()).data.user?.id)
        .single();

      onSuccess(profile?.user_type || "customer");
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;
    const fullName = formData.get("fullName") as string;
    const phone = formData.get("phone") as string;

    try {
      const { data: authData, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `https://sortable.uk/`,
          data: {
            full_name: fullName,
            user_type: userType,
            phone,
            address,
          },
        },
      });

      if (error) throw error;

      // For tradesman accounts, redirect to profile setup
      if (userType === 'tradesman' && authData.user) {
        console.log('Tradesman account created - redirecting to profile setup');
        toast({
          title: "Account created!",
          description: "Please complete your profile setup and upload required documents to activate your account.",
        });
        navigate('/tradesman-profile-setup');
        return;
      }

      // For customer accounts, show success and redirect to home
      toast({
        title: "Account created!",
        description: "Welcome! Your account has been created successfully.",
      });
      onSuccess(userType);
    } catch (error: any) {
      toast({
        title: "Sign up failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    if (!resendEmail) {
      toast({
        title: "Email required",
        description: "Please enter your email address.",
        variant: "destructive",
      });
      return;
    }

    setIsResending(true);
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email: resendEmail,
        options: {
          emailRedirectTo: `https://sortable.uk/`,
        },
      });

      if (error) throw error;

      toast({
        title: "Confirmation email sent!",
        description: "Please check your email (including spam folder) for the confirmation link.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsResending(false);
    }
  };

  const handleSendMagicLink = async () => {
    if (!magicEmail) {
      toast({
        title: "Email required",
        description: "Please enter your email address.",
        variant: "destructive",
      });
      return;
    }

    setIsSendingMagic(true);
    try {
      // Use our custom send-magic-link edge function with Resend
      const { error } = await supabase.functions.invoke('send-magic-link', {
        body: { email: magicEmail }
      });

      if (error) {
        console.error('Magic link error:', error);
        throw new Error(error.message || 'Failed to send magic link');
      }

      toast({
        title: "Magic link sent!",
        description: "Check your email for the sign in link.",
      });
    } catch (error: any) {
      console.error('Magic link error:', error);
      toast({
        title: "Error", 
        description: error.message || "Failed to send magic link. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSendingMagic(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!magicEmail) {
      toast({
        title: "Email required",
        description: "Please enter your email address in the magic link field above.",
        variant: "destructive",
      });
      return;
    }

    setIsSendingMagic(true);
    try {
      const { error } = await supabase.functions.invoke('send-password-reset', {
        body: { email: magicEmail }
      });

      if (error) throw error;

      toast({
        title: "Password reset email sent!",
        description: "Check your email for the password reset link.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to send password reset email.",
        variant: "destructive",
      });
    } finally {
      setIsSendingMagic(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50/30 via-background to-blue-100/20 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background decorative elements matching job card style */}
      <div className="absolute inset-0 bg-gradient-subtle opacity-50"></div>
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-blue opacity-20 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-gradient-hero opacity-10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      
      <div className="w-full max-w-md sm:max-w-2xl">
        {/* Flex layout for desktop */}
        <div className="sm:flex sm:min-h-[500px]">
          <div className="text-center mb-8 sm:hidden">
            <div className="flex justify-between items-center mb-4 relative">
              <Button
                variant="ghost"
                className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 transition-all duration-300 flex items-center gap-2 relative z-30"
                size="sm"
                asChild
              >
                <Link to="/">
                  <ArrowLeft className="w-4 h-4" />
                  Home
                </Link>
              </Button>
              
              <div className="relative z-20">
                <ModernLogo size="md" showTagline={false} />
              </div>
              
              <div className="w-[80px]" /> {/* Spacer for centering */}
            </div>
            <p className="text-muted-foreground">Fast tradesman response guaranteed</p>
            <div className="flex items-center justify-center gap-4 mt-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <Phone className="h-4 w-4" />
                <span>0800 043 8880</span>
              </div>
              <div className="flex items-center gap-1">
                <Mail className="h-4 w-4" />
                <span>realise@post.com</span>
              </div>
            </div>
          </div>

          <Card className="shadow-elegant hover:shadow-glow transition-all duration-300 border-0 bg-gradient-blue text-white animate-fade-in relative z-10">
            <div className="sm:grid sm:grid-cols-5 sm:min-h-[500px]">
              {/* Left side - Header (desktop) / Top (mobile) */}
              <div className="sm:col-span-2 sm:bg-gradient-to-br sm:from-white/10 sm:to-blue-500/20 sm:p-8 sm:flex sm:flex-col sm:justify-center backdrop-blur-sm">
                <div className="hidden sm:flex justify-center mb-6">
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/')}
                    className="text-white hover:text-blue-100 hover:bg-white/10 transition-all duration-300 flex items-center gap-2 absolute top-4 left-4"
                    size="sm"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Home
                  </Button>
                </div>
                
                <CardHeader className="text-center space-y-3 pb-6 sm:bg-none sm:pb-0">
                  <div className="mx-auto w-16 h-16 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center mb-2 shadow-glow animate-scale-in">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <CardTitle className="text-3xl font-bold text-white">Get Started</CardTitle>
                  <CardDescription className="text-lg text-white/90">
                    Choose your account type and sign in or create an account
                  </CardDescription>
                </CardHeader>
              </div>

              {/* Right side - Content (desktop) / Bottom (mobile) */}
              <div className="sm:col-span-3 sm:flex sm:flex-col sm:justify-center">
                <CardContent className="space-y-6 p-6 sm:p-8 bg-white/95 backdrop-blur-md shadow-elegant animate-fade-in border-0">
                  <Tabs defaultValue="signin" className="space-y-6">
                    <TabsList className="grid w-full grid-cols-2 h-12 bg-gradient-to-r from-blue-100 to-blue-50 border-0 shadow-md">
                      <TabsTrigger value="signin" className="text-sm font-medium data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-md transition-all duration-300">Sign In</TabsTrigger>
                      <TabsTrigger value="signup" className="text-sm font-medium data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-md transition-all duration-300">Sign Up</TabsTrigger>
                    </TabsList>

                    <TabsContent value="signin" className="animate-fade-in">
                      <form onSubmit={handleSignIn} className="space-y-5">
                        <div className="space-y-2">
                          <Label htmlFor="signin-email" className="text-sm font-medium">Email</Label>
                          <Input
                            id="signin-email"
                            name="email"
                            type="email"
                            placeholder="Enter your email"
                            className="h-11 transition-all duration-300 focus:ring-2 focus:ring-blue-500/50 border-blue-200 hover:border-blue-300 bg-white shadow-sm text-black placeholder:text-muted-foreground"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="signin-password" className="text-sm font-medium">Password</Label>
                          <div className="relative">
                            <Input
                              id="signin-password"
                              name="password"
                              type={showSignInPassword ? "text" : "password"}
                              placeholder="Enter your password"
                              className="h-11 transition-all duration-300 focus:ring-2 focus:ring-blue-500/50 border-blue-200 hover:border-blue-300 bg-white shadow-sm text-black placeholder:text-muted-foreground pr-10"
                              required
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="absolute right-0 top-0 h-11 px-3 hover:bg-blue-50 transition-colors duration-200"
                              onClick={() => setShowSignInPassword(!showSignInPassword)}
                            >
                              {showSignInPassword ? (
                                <EyeOff className="h-4 w-4 text-blue-600" />
                              ) : (
                                <Eye className="h-4 w-4 text-blue-600" />
                              )}
                            </Button>
                          </div>
                        </div>
                        <Button type="submit" className="w-full h-12 text-base font-medium bg-gradient-blue text-white shadow-glow hover:shadow-elegant transition-all duration-300 border-0" disabled={isLoading} size="lg">
                          {isLoading ? "Signing in..." : "Sign In"}
                        </Button>
                      </form>

                      <div className="mt-6 space-y-4">
                        <div className="p-4 rounded-lg bg-gradient-to-r from-blue-50 to-blue-100/50 border border-blue-200 shadow-sm">
                          <p className="text-sm font-medium mb-3 text-center text-blue-900">Quick Options</p>
                          <div className="space-y-3">
                            <div>
                              <p className="text-sm mb-2 text-blue-800">Can't confirm your email? Resend the confirmation link:</p>
                              <div className="flex gap-2">
                                <Input
                                  value={resendEmail}
                                  onChange={(e) => setResendEmail(e.target.value)}
                                  type="email"
                                  placeholder="Enter your email"
                                  className="h-10 transition-all duration-300 focus:ring-2 focus:ring-blue-500/50 border-blue-200 hover:border-blue-300 bg-white text-black placeholder:text-muted-foreground"
                                  aria-label="Email for resending confirmation"
                                />
                                <Button onClick={handleResendConfirmation} disabled={isResending} className="w-full h-12 text-base font-medium bg-gradient-blue text-white shadow-glow hover:shadow-elegant transition-all duration-300 border-0">
                                  {isResending ? "Sending..." : "Resend"}
                                </Button>
                              </div>
                            </div>

                            <div className="border-t border-blue-200 pt-3">
                              <p className="text-sm mb-2 text-blue-800">Prefer passwordless? We'll email you a magic login link:</p>
                              <div className="flex gap-2 mb-3">
                                <Input
                                  value={magicEmail}
                                  onChange={(e) => setMagicEmail(e.target.value)}
                                  type="email"
                                  placeholder="Enter your email"
                                  className="h-10 transition-all duration-300 focus:ring-2 focus:ring-blue-500/50 border-blue-200 hover:border-blue-300 bg-white text-black placeholder:text-muted-foreground"
                                  aria-label="Email for magic link sign-in"
                                />
                                <Button onClick={handleSendMagicLink} disabled={isSendingMagic} variant="secondary" className="px-4 transition-all duration-300 hover:shadow-md bg-blue-100 text-blue-700 hover:bg-blue-200 border-blue-300">
                                  {isSendingMagic ? "Sending..." : "Magic Link"}
                                </Button>
                              </div>
                              <div className="border-t border-blue-200 pt-3">
                                <p className="text-sm mb-2 text-blue-800">Forgot your password? Reset it here:</p>
                                <Button onClick={handlePasswordReset} disabled={isSendingMagic} className="w-full h-12 text-base font-medium bg-gradient-blue text-white shadow-glow hover:shadow-elegant transition-all duration-300 border-0">
                                  {isSendingMagic ? "Please wait..." : "Reset Password"}
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="signup" className="animate-fade-in">
                      <form onSubmit={handleSignUp} className="space-y-5">
                        <div className="space-y-3">
                          <Label className="text-sm font-medium">I am a:</Label>
                          <RadioGroup
                            value={userType}
                            onValueChange={(value) => setUserType(value as "customer" | "tradesman")}
                            className="flex gap-8 justify-center"
                          >
                             <div className="flex items-center space-x-2 p-3 rounded-lg border-2 border-blue-200 hover:border-blue-400 hover:bg-blue-50 transition-all duration-300 cursor-pointer shadow-sm">
                               <RadioGroupItem value="customer" id="customer" />
                               <Label htmlFor="customer" className="cursor-pointer font-medium text-black">Customer</Label>
                             </div>
                             <div className="flex items-center space-x-2 p-3 rounded-lg border-2 border-blue-200 hover:border-blue-400 hover:bg-blue-50 transition-all duration-300 cursor-pointer shadow-sm">
                               <RadioGroupItem value="tradesman" id="tradesman" />
                               <Label htmlFor="tradesman" className="cursor-pointer font-medium text-black">Tradesman</Label>
                            </div>
                          </RadioGroup>
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="fullName" className="text-sm font-medium">Full Name</Label>
                          <Input
                            id="fullName"
                            name="fullName"
                            type="text"
                            placeholder="Enter your full name"
                            className="h-11 transition-all duration-300 focus:ring-2 focus:ring-blue-500/50 border-blue-200 hover:border-blue-300 bg-white shadow-sm text-black placeholder:text-muted-foreground"
                            required
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="signup-email" className="text-sm font-medium">Email</Label>
                          <Input
                            id="signup-email"
                            name="email"
                            type="email"
                            placeholder="Enter your email"
                            className="h-11 transition-all duration-300 focus:ring-2 focus:ring-blue-500/50 border-blue-200 hover:border-blue-300 bg-white shadow-sm text-black placeholder:text-muted-foreground"
                            required
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="phone" className="text-sm font-medium">Phone Number</Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            placeholder="e.g., 07123456789 or 01234567890"
                            className="h-11 transition-all duration-300 focus:ring-2 focus:ring-blue-500/50 border-blue-200 hover:border-blue-300 bg-white shadow-sm text-black placeholder:text-muted-foreground"
                            required
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="address" className="text-sm font-medium">Address</Label>
                          <GooglePlacesAddress
                            id="address"
                            name="address"
                            value={address}
                            onChange={setAddress}
                            placeholder="Enter your full address"
                            className="h-11 transition-all duration-300 focus:ring-2 focus:ring-blue-500/50 border-blue-200 hover:border-blue-300 bg-white shadow-sm text-black placeholder:text-muted-foreground"
                            required
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="signup-password" className="text-sm font-medium">Password</Label>
                          <div className="relative">
                            <Input
                              id="signup-password"
                              name="password"
                              type={showSignUpPassword ? "text" : "password"}
                              placeholder="Create a password"
                              className="h-11 transition-all duration-300 focus:ring-2 focus:ring-blue-500/50 border-blue-200 hover:border-blue-300 bg-white shadow-sm text-black placeholder:text-muted-foreground pr-10"
                              required
                              minLength={6}
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="absolute right-0 top-0 h-11 px-3 hover:bg-blue-50 transition-colors duration-200"
                              onClick={() => setShowSignUpPassword(!showSignUpPassword)}
                            >
                              {showSignUpPassword ? (
                                <EyeOff className="h-4 w-4 text-blue-600" />
                              ) : (
                                <Eye className="h-4 w-4 text-blue-600" />
                              )}
                            </Button>
                          </div>
                        </div>
                        
                        <Button
                          type="submit"
                          className="w-full h-12 text-base font-medium bg-gradient-blue text-white shadow-glow hover:shadow-elegant transition-all duration-300 border-0"
                          disabled={isLoading}
                          size="lg"
                        >
                          {isLoading ? "Creating account..." : `Sign Up as ${userType === "customer" ? "Customer" : "Tradesman"}`}
                        </Button>
                      </form>
                    </TabsContent>
                  </Tabs>

                  <div className="mt-8 p-4 bg-gradient-to-r from-blue-50 to-blue-100/50 rounded-lg border border-blue-200 shadow-sm">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                      <div className="text-sm text-blue-800">
                        <p className="font-medium">Sortable™</p>
                        <p>Trademark of Realise Property Maintenance Ltd.</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};